import { Injectable } from '@angular/core';
import { TemplatePrintOptions } from 'src/app/models/reportTemplate.model';
import { TemplateContext } from 'src/app/models/reportTemplate.model';
import { TemplateProcessorService } from '../report-generation-popup/services/template-processor.service';
import { PdfGeneratorService } from '../report-generation-popup/services/pdf-generator.service';
import { stripChipMarkupForStorage, preprocessHtmlForEditor, humanizePlaceholderChips } from 'src/app/shared/rich-text-editor/placeholder-chip.plugin';
import { getAllWidgets } from 'src/app/shared/widget-registry';
import { buildPreviewResetCss } from '../report-generation-popup/services/preview-css.constants';
import { TemplateMapperRegistry } from 'src/app/services/template-mappers/template-mapper-registry';
import { environment } from 'src/environments/environment';

const PREVIEW_PAGE_CLASS = 'rte-shell__preview-page';

const PREVIEW_EXTRA_CSS = `
${buildPreviewResetCss(`.${PREVIEW_PAGE_CLASS}`)}
.${PREVIEW_PAGE_CLASS} * { white-space: normal !important; }
.${PREVIEW_PAGE_CLASS} > div { box-shadow: none !important; }
`;

export interface LivePreviewInputs {
  html: string;
  entityType: string;
  templateContext: TemplateContext;
  printOptions: TemplatePrintOptions | null | undefined;
  // Render widgets with their structural shell when no record is selected (management-grid View).
  previewMode?: boolean;
}

@Injectable()
export class LivePreviewBuilderService {
  constructor(
    private templateProcessor: TemplateProcessorService,
    private pdfGenerator: PdfGeneratorService,
    private mapperRegistry: TemplateMapperRegistry
  ) {}

  async buildHtml(inputs: LivePreviewInputs): Promise<string> {
    const cleanedHtml = stripChipMarkupForStorage(inputs.html);

    if (inputs.previewMode) {
      const structuralHtml = humanizePlaceholderChips(preprocessHtmlForEditor(cleanedHtml));
      return inputs.printOptions ? this.wrapInPage(structuralHtml, inputs.printOptions, inputs.entityType) : structuralHtml;
    }

    const processedHtml = this.templateProcessor.render(cleanedHtml, inputs.templateContext).html;

    const tempContainer = document.createElement('div');
    document.body.appendChild(tempContainer);
    try {
      tempContainer.innerHTML = processedHtml;
      const transientRefs = await this.pdfGenerator.injectWidgets(
        tempContainer, inputs.entityType, inputs.templateContext, false, inputs.previewMode
      );
      await this.pdfGenerator.waitForWidgets(transientRefs);
      const injectedHtml = tempContainer.innerHTML;
      transientRefs.forEach(ref => ref.destroy());
      return inputs.printOptions ? this.wrapInPage(injectedHtml, inputs.printOptions, inputs.entityType) : injectedHtml;
    } catch (error) {
      if (!environment.production) {
        console.warn('Live preview widget injection failed; rendering placeholders', error);
      }
      const fallbackHtml = this.replaceWidgetTagsWithPlaceholders(processedHtml);
      return inputs.printOptions ? this.wrapInPage(fallbackHtml, inputs.printOptions, inputs.entityType) : fallbackHtml;
    } finally {
      document.body.removeChild(tempContainer);
    }
  }

  private buildEntityExtraCss(entityType: string): string {
    return this.mapperRegistry.getMapper(entityType)?.getEntityPreviewCss(`.${PREVIEW_PAGE_CLASS}`) ?? '';
  }

  private wrapInPage(processedHtml: string, opts: TemplatePrintOptions, entityType?: string): string {
    const headerText = this.escapeHtml(opts.headerText?.trim() ?? '');
    const footerText = this.escapeHtml(opts.footerText?.trim() ?? '');
    const watermark = this.escapeHtml(opts.watermarkText?.trim() ?? '');
    const today = new Date().toLocaleDateString('en-GB');
    const dateHint = opts.showGenerationDate ? `Generated: ${today}` : '';
    const pageHint = opts.showPageNumbers ? 'Page 1 of N (PDF only)' : '';

    const headerBar = headerText
      ? `<div style="text-align:center;font-size:0.75rem;color:#6b7280;padding:4px 0 8px;border-bottom:1px solid #e5e7eb;margin-bottom:8px;">${headerText}</div>`
      : '';

    const footerParts = [footerText, dateHint, pageHint].filter(Boolean);
    const footerBar = footerParts.length
      ? `<div style="text-align:center;font-size:0.75rem;color:#6b7280;padding:8px 0 4px;border-top:1px solid #e5e7eb;margin-top:8px;">${footerParts.join(' &nbsp;|&nbsp; ')}</div>`
      : '';

    const watermarkOverlay = watermark
      ? `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;overflow:hidden;z-index:0;">
           <span style="font-size:4rem;font-weight:700;color:rgba(0,0,0,0.08);transform:rotate(-45deg);white-space:nowrap;user-select:none;">${watermark}</span>
         </div>`
      : '';

    const entityCss = entityType ? this.buildEntityExtraCss(entityType) : '';
    return `<style>${PREVIEW_EXTRA_CSS}${entityCss}</style>${watermarkOverlay}${headerBar}${processedHtml}${footerBar}`;
  }

  private replaceWidgetTagsWithPlaceholders(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;

    for (const widget of getAllWidgets()) {
      const placeholder = `<div class="lp-widget-placeholder"><span class="lp-widget-icon material-icons" style="font-size:20px;">widgets</span><span>${widget.displayLabel}</span></div>`;
      container.querySelectorAll<HTMLElement>(widget.tagName).forEach(el => {
        const ph = document.createElement('div');
        ph.innerHTML = placeholder;
        el.parentNode?.replaceChild(ph.firstElementChild!, el);
      });
    }

    return container.innerHTML;
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
